import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { LeaveRequestCreateDto, MyEligibleLeaveTypesParams } from '@/utils/api/leave-service/models'

import { UserSearchInput } from '@/components/common/UserSearchInput'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser } from '@/lib/role-guards'
import { handleError } from '@/lib/utils'
import { useMyEligibleLeaveTypes, useSubmitLeaveAsAdmin } from '@/utils/api/leave-service'

import { LeaveRequestForm } from './LeaveRequestForm'

interface SubmitLeaveForUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function SubmitLeaveForUserDialog({
  open,
  onOpenChange,
  onSuccess,
}: SubmitLeaveForUserDialogProps) {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = isAdminUser(currentUser)
  const [adminRequestUserId, setAdminRequestUserId] = useState<number | undefined>(undefined)
  const [adminRequestFormKey, setAdminRequestFormKey] = useState(0)
  const submitLeaveAsAdminMutation = useSubmitLeaveAsAdmin()
  const leaveTypesParams = useMemo<MyEligibleLeaveTypesParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])
  const myEligibleLeaveTypesQuery = useMyEligibleLeaveTypes(leaveTypesParams)

  const resetForm = useCallback(() => {
    setAdminRequestUserId(undefined)
    setAdminRequestFormKey(prev => prev + 1)
  }, [])

  const handleDialogChange = useCallback((nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      resetForm()
    }
  }, [onOpenChange, resetForm])

  const handleAdminSubmit = useCallback(async (payload: LeaveRequestCreateDto) => {
    if (!adminRequestUserId) {
      throw new Error('Select a staff member to submit leave for.')
    }

    const response = await submitLeaveAsAdminMutation.mutateAsync({
      userId: adminRequestUserId,
      data: payload,
    })

    if (!response?.data?.success) {
      throw new Error(response?.data?.message || 'Unable to submit leave request.')
    }

    toast.success(response.data.message || 'Leave request submitted successfully.')
    handleDialogChange(false)
    await queryClient.invalidateQueries({ queryKey: ['allLeaveRequests'] })
    onSuccess?.()
  }, [adminRequestUserId, handleDialogChange, onSuccess, queryClient, submitLeaveAsAdminMutation])

  if (myEligibleLeaveTypesQuery.isError) {
    handleError(myEligibleLeaveTypesQuery.error || 'Failed to load eligible leave types. Please try again later.')
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submit Leave for Staff</DialogTitle>
          <DialogDescription>
            Submit a leave request on behalf of another staff member.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <UserSearchInput
            id="admin-leave-request-user"
            label="Staff Member"
            placeholder="Search user by email..."
            required
            value={adminRequestUserId}
            onValueChange={setAdminRequestUserId}
            disableOrganisationFilter={isAdmin}
          />
          <LeaveRequestForm
            key={adminRequestFormKey}
            submitLabel="Submit Request"
            resetLabel="Reset Form"
            disableUntilUserSelected
            allowPastDates
            eligibleUserId={adminRequestUserId}
            onSubmitOverride={handleAdminSubmit}
            myEligibleLeaveTypesData={myEligibleLeaveTypesQuery.data?.data?.content ?? []}
            leaveTypesLoading={myEligibleLeaveTypesQuery.isLoading}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
